import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { COUNTRIES } from '../constants';
import { CPV_TREE } from '../data/cpv';

const s = {
  page: { maxWidth: 820, margin: '32px auto', padding: '0 24px 60px' },
  heading: { fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  subheading: { fontSize: 15, color: '#64748b', marginBottom: 24 },
  gateBanner: {
    background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12,
    padding: '24px 28px', textAlign: 'center', marginTop: 40,
  },
  gateTitle: { fontSize: 18, fontWeight: 600, color: '#92400e', marginBottom: 8 },
  gateText: { fontSize: 14, color: '#78350f', marginBottom: 16 },
  upgradeLink: {
    display: 'inline-block', background: '#1e40af', color: '#fff',
    fontWeight: 600, fontSize: 14, padding: '10px 24px', borderRadius: 8, textDecoration: 'none',
  },
  filters: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: '20px 24px', marginBottom: 20,
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
  },
  filtersFull: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: '20px 24px', marginBottom: 20,
  },
  filterLabel: { fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 },
  input: {
    width: '100%', padding: '9px 12px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '9px 12px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none',
    background: '#fff', boxSizing: 'border-box', cursor: 'pointer',
  },
  searchBtn: {
    background: '#1e40af', color: '#fff', border: 'none', fontWeight: 600,
    fontSize: 14, padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
  },
  clearBtn: {
    background: '#fff', color: '#64748b', border: '1px solid #e2e8f0',
    fontWeight: 500, fontSize: 14, padding: '10px 16px', borderRadius: 8, cursor: 'pointer',
  },
  card: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '16px 20px', marginBottom: 12,
    transition: 'box-shadow 0.15s',
  },
  cardTitle: { fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 8, lineHeight: 1.4 },
  cardMeta: { display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#64748b' },
  badge: (bg, color) => ({
    background: bg, color, fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 12,
  }),
  cardLink: {
    display: 'inline-block', marginTop: 10, fontSize: 13, color: '#2563eb',
    fontWeight: 500, textDecoration: 'none',
  },
  pagination: { display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 20 },
  pageBtn: (active) => ({
    padding: '7px 14px', borderRadius: 7, fontSize: 13, cursor: 'pointer',
    border: active ? 'none' : '1px solid #e2e8f0',
    background: active ? '#1e40af' : '#fff',
    color: active ? '#fff' : '#374151',
    fontWeight: active ? 600 : 400,
  }),
  loadingBox: { textAlign: 'center', padding: '60px 0', color: '#64748b', fontSize: 15 },
  emptyBox: {
    textAlign: 'center', padding: '60px 20px',
    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
    color: '#64748b', fontSize: 14,
  },
  resultsBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  resultsCount: { fontSize: 14, color: '#64748b' },
};

const NOTICE_TYPES = [
  { value: '', label: 'All types' },
  { value: 'cn-standard', label: 'Contract Notice' },
  { value: 'can-standard', label: 'Contract Award' },
  { value: 'pin-only', label: 'Prior Information' },
];

function formatDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function formatValue(val) {
  if (!val) return null;
  return '€' + Number(val).toLocaleString('en-EU');
}

// Flat list of CPV options for the picker
const CPV_OPTIONS = [{ code: '', label: 'All CPV sectors' }].concat(
  CPV_TREE.flatMap((div) => [
    { code: div.code, label: `${div.code} — ${div.label}` },
    ...div.groups.map((g) => ({ code: g.code, label: `  ${g.code} — ${g.label}` })),
  ])
);

export default function TenderSearch() {
  // Access check, cached so revisiting Search doesn't re-flash "Loading…".
  const accessQuery = useQuery({
    queryKey: ['tender-search-access'],
    queryFn: () => apiFetch('/tenders/search?q=&per_page=1'),
    retry: false,
  });
  const checking = accessQuery.isLoading;
  const accessForbidden = !!accessQuery.error
    && (accessQuery.error.message.includes('403') || accessQuery.error.message.includes('Scale'));

  const [forbidden, setForbidden] = useState(false);

  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [cpv, setCpv] = useState('');
  const [noticeType, setNoticeType] = useState('');
  const [page, setPage] = useState(1);

  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const PER_PAGE = 20;

  const doSearch = useCallback(async (p = 1) => {
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (country) params.set('country', country);
      if (cpv) params.set('cpv', cpv);
      if (noticeType) params.set('notice_type', noticeType);
      params.set('page', p);
      params.set('per_page', PER_PAGE);

      const data = await apiFetch(`/tenders/search?${params}`);
      setResults(data?.results || []);
      setHasMore(data?.has_more || false);
      setPage(p);
    } catch (e) {
      if (e.message.includes('403') || e.message.includes('Scale')) setForbidden(true);
    } finally {
      setLoading(false);
    }
  }, [q, country, cpv, noticeType]);

  function handleClear() {
    setQ(''); setCountry(''); setCpv(''); setNoticeType('');
    setResults([]); setHasMore(false); setSearched(false); setPage(1);
  }

  if (checking) return <div style={{ ...s.page, color: '#64748b', paddingTop: 60 }}>Loading…</div>;

  if (forbidden || accessForbidden) {
    return (
      <div style={s.page}>
        <h1 style={s.heading}>Browse Tenders</h1>
        <div style={s.gateBanner}>
          <div style={s.gateTitle}>Scale plan required</div>
          <div style={s.gateText}>
            The Browse Tenders portal lets you search the full TED database by keyword, country, CPV code, and procurement type —
            without waiting for the nightly matching run.
          </div>
          <Link to="/billing" style={s.upgradeLink}>Upgrade to Scale</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Browse Tenders</h1>
      <p style={s.subheading}>Search the full TED procurement database directly.</p>

      {/* Filters */}
      <div style={s.filtersFull}>
        <div style={{ marginBottom: 14 }}>
          <div style={s.filterLabel}>Keywords</div>
          <input
            style={s.input}
            placeholder="e.g. cloud infrastructure, cybersecurity audit…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch(1)}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <div style={s.filterLabel}>Country</div>
            <select style={s.select} value={country} onChange={(e) => setCountry(e.target.value)}>
              <option value="">All countries</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div style={s.filterLabel}>CPV sector</div>
            <select style={s.select} value={cpv} onChange={(e) => setCpv(e.target.value)}>
              {CPV_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <div style={s.filterLabel}>Notice type</div>
            <select style={s.select} value={noticeType} onChange={(e) => setNoticeType(e.target.value)}>
              {NOTICE_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={s.searchBtn} onClick={() => doSearch(1)}>Search</button>
          {searched && <button style={s.clearBtn} onClick={handleClear}>Clear</button>}
        </div>
      </div>

      {/* Results */}
      {loading && <div style={s.loadingBox}>Searching…</div>}

      {!loading && searched && (
        <>
          <div style={s.resultsBar}>
            <span style={s.resultsCount}>
              {results.length === 0 ? 'No results' : `${results.length} result${results.length !== 1 ? 's' : ''}${hasMore ? '+' : ''} on page ${page}`}
            </span>
          </div>

          {results.length === 0 ? (
            <div style={s.emptyBox}>No tenders match your filters. Try broadening your search.</div>
          ) : (
            <>
              {results.map((t) => {
                const days = t.deadline ? Math.ceil((new Date(t.deadline) - new Date()) / 86400000) : null;
                return (
                  <div key={t.id} style={s.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                      <div style={s.cardTitle}>{t.title || 'Untitled Tender'}</div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        {t.contract_nature && (
                          <span style={s.badge('#f0fdf4', '#15803d')}>{t.contract_nature}</span>
                        )}
                        {days !== null && days <= 7 && (
                          <span style={s.badge('#fef2f2', '#dc2626')}>Closing soon</span>
                        )}
                      </div>
                    </div>
                    <div style={s.cardMeta}>
                      {t.country && <span>{t.country}</span>}
                      {t.deadline && <span>Deadline: {formatDate(t.deadline)}{days !== null ? ` (${days > 0 ? `${days}d` : 'expired'})` : ''}</span>}
                      {(t.contract_value || t.estimated_value) && <span>{formatValue(t.contract_value || t.estimated_value)}</span>}
                      {t.buyer_name && <span>{t.buyer_name}</span>}
                    </div>
                    {t.description_en && (
                      <div style={{ fontSize: 13, color: '#64748b', marginTop: 8, lineHeight: 1.5,
                        overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {t.description_en}
                      </div>
                    )}
                    <Link to={`/tender/${t.id}`} style={s.cardLink}>View details →</Link>
                  </div>
                );
              })}

              {(page > 1 || hasMore) && (
                <div style={s.pagination}>
                  <button style={s.pageBtn(false)} onClick={() => doSearch(page - 1)} disabled={page <= 1}>← Prev</button>
                  <span style={{ fontSize: 13, color: '#64748b', padding: '0 8px' }}>Page {page}</span>
                  <button style={s.pageBtn(false)} onClick={() => doSearch(page + 1)} disabled={!hasMore}>Next →</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {!loading && !searched && (
        <div style={{ ...s.emptyBox, padding: '40px 20px' }}>
          Enter keywords or select filters above, then click Search.
        </div>
      )}
    </div>
  );
}
